"use client";

import { GenderTexts, MaritalStatusTexts, PhysicalTexts } from "@entities/common";
import { EmployerType } from "@entities/employer";
import { Proposal, ProposalStatusCard } from "@entities/proposal";
import {
  Vacancy,
  VacancyableType,
  VacancyableTypeTexts,
  VacancyExpiringAlert,
  VacancyGovernmentCard,
  VacancyIndividualEmployerCard,
  VacancySalaryInfo,
} from "@entities/vacancy";
import { VacancyCompanyCard } from "@entities/vacancy/ui/vacancy-company-card";
import { TDomesticVacancyDetailPageMeta, TVacancyCard } from "@shared/cms";
import { DetailItem, HtmlParser } from "@shared/ui";

export function VacancyDetailSection({
  vacancy,
  meta,
  vacancyCardMeta,
  activeProposal,
}: {
  vacancy: Vacancy;
  meta: TDomesticVacancyDetailPageMeta;
  vacancyCardMeta: TVacancyCard;
  activeProposal?: Proposal | null;
}) {
  return (
    <section className="relative mt-6 sm:mt-10">
      <div className="container">
        <div className="flex flex-col-reverse md:flex-row md:space-x-6">
          <div className="flex-1 overflow-hidden">
            <VacancyExpiringAlert vacancy={vacancy} />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 text-sm gap-x-2 gap-y-5 sm:gap-x-6 sm:gap-y-6 mb-6 sm:mb-10">
              {vacancy.jobFunction?.name && <DetailItem label={meta.jobFunctionLabel} value={vacancy.jobFunction?.name} />}
              {vacancy.jobType?.name && <DetailItem label={meta.jobTypeLabel} value={vacancy.jobType?.name} />}
              <DetailItem label={meta.vacancyableTypeLabel} value={VacancyableTypeTexts[vacancy.vacancyableType]} />
              <DetailItem label={meta.genderLabel} value={GenderTexts[vacancy.gender === null ? "other" : vacancy.gender]} />
              <DetailItem
                label={meta.salaryLabel}
                value={
                  <VacancySalaryInfo
                    show={vacancy.showSalary}
                    meta={meta.salaryInfo}
                    min={vacancy.minSalaryAmount || vacancy.job?.minBudgetAmount}
                    max={vacancy.maxSalaryAmount || vacancy.job?.maxBudgetAmount}
                    currency={vacancy.minSalaryCurrency || vacancy.job?.minBudget?.currency}
                    className="!text-sm text-gray-700"
                  />
                }
              />
            </div>
            <hr className="bg-gray-100 -ml-6 md:ml-0 w-screen md:w-full mb-6 sm:mb-10" />
            <div className="flex flex-col space-y-4 mb-6 sm:mb-10">
              <div className="text-gray-700 text-lg sm:text-xl font-bold">{meta.jobDescriptionLabel}</div>
              <div className="flex flex-col space-y-4 text-gray-700 text-sm leading-[180%]">
                <div className="overflow-hidden">
                  <HtmlParser data={vacancy.description || vacancy.job?.description} />
                </div>
              </div>
            </div>
            <div className="flex flex-col space-y-4 mb-6 sm:mb-10">
              <div className="text-gray-700 text-lg sm:text-xl font-bold">{meta.specialRequirementsLabel}</div>
              <div className="flex flex-col space-y-4 text-gray-700 text-sm leading-[180%]">
                <div className="overflow-hidden">
                  <HtmlParser data={vacancy.qualification} />
                </div>
              </div>
            </div>
            <div className="flex flex-col space-y-4 mb-6 sm:mb-10">
              <div className="text-gray-700 text-lg sm:text-xl font-bold">{meta.generalRequirementsLabel}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-5 sm:gap-x-6 sm:gap-y-6 text-sm">
                <DetailItem label={meta.educationLabel} value={vacancy.minEducation?.name || vacancy.education?.name} />
                {vacancy.marital !== undefined ? (
                  <DetailItem
                    label={meta.maritalStatusLabel}
                    value={MaritalStatusTexts[vacancy.marital === null ? "other" : vacancy.marital]}
                  />
                ) : (
                  <DetailItem
                    label={meta.maritalStatusLabel}
                    value={MaritalStatusTexts[vacancy.maritalStatus === null ? "other" : vacancy.maritalStatus]}
                  />
                )}
                <DetailItem
                  label={meta.minExperienceLabel}
                  value={
                    vacancy.minYearExperience || vacancy.micro?.minExperience
                      ? `${vacancy.minYearExperience || vacancy.micro?.minExperience} Tahun`
                      : "Tidak ditentukan"
                  }
                />
                {vacancy.physical !== undefined ? (
                  <DetailItem
                    label={meta.physicalConditionLabel}
                    value={PhysicalTexts[vacancy.physical === null ? "other" : vacancy.physical]}
                  />
                ) : (
                  <DetailItem
                    label={meta.physicalConditionLabel}
                    value={PhysicalTexts[vacancy.physicalCondition === null ? "other" : vacancy.physicalCondition]}
                  />
                )}
              </div>
            </div>
            {meta.skillsLabel && vacancy.skills && vacancy.skills.length > 0 ? (
              <div className="flex flex-col space-y-4 mb-6 sm:mb-10">
                <div className="text-gray-700 text-lg sm:text-xl font-bold">{meta.skillsLabel}</div>
                <div className="flex flex-wrap items-center">
                  {vacancy.skills.map((skill, index) => {
                    return (
                      <span
                        key={skill + "-" + index + Math.random()}
                        className="border border-gray-300 rounded-full text-gray-500 py-0.5 px-2 text-sm font-medium whitespace-nowrap mr-2 mb-2 relative capitalize"
                      >
                        {skill}
                      </span>
                    );
                  })}
                </div>
              </div>
            ) : (
              meta.skillsLabel &&
              vacancy.job?.skills &&
              vacancy.job?.skills?.length > 0 && (
                <div className="flex flex-col space-y-4 mb-6 sm:mb-10">
                  <div className="text-gray-700 text-lg sm:text-xl font-bold">{meta.skillsLabel}</div>
                  <div className="flex flex-wrap items-center">
                    {vacancy.job.skills.map((skill, index) => {
                      return (
                        <span
                          key={skill + "-" + index + Math.random()}
                          className="border border-gray-300 rounded-full text-gray-500 py-0.5 px-2 text-sm font-medium whitespace-nowrap mr-2 mb-2 relative capitalize"
                        >
                          {skill}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )
            )}
            {vacancy.employer?.employerable?.about && (
              <div className="flex flex-col space-y-4">
                <div className="text-gray-700 text-lg sm:text-xl font-bold">{meta.companyAboutLabel || "Tentang Perusahaan"}</div>
                <div className="flex flex-col space-y-4 text-gray-700 text-sm leading-[180%]">
                  <div>
                    <HtmlParser data={vacancy.employer?.employerable?.about} />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="md:w-[264px] md:min-w-[264px] pb-6">
            <div className="sticky top-[88px] sm:top-[104px] space-y-6">
              {activeProposal && <ProposalStatusCard vacancy={vacancy} proposal={activeProposal} />}
              {vacancy.vacancyableType === VacancyableType.MICRO_VACANCY ? (
                <VacancyIndividualEmployerCard
                  employer={vacancy.job?.employer || vacancy.employer}
                  vacancy={vacancy}
                  meta={meta}
                  vacancyCardMeta={vacancyCardMeta}
                />
              ) : vacancy.employer.employerableType === EmployerType.COMPANY ? (
                <VacancyCompanyCard employer={vacancy.employer} vacancy={vacancy} meta={meta} vacancyCardMeta={vacancyCardMeta} />
              ) : (
                <VacancyGovernmentCard employer={vacancy.employer} vacancy={vacancy} meta={meta} vacancyCardMeta={vacancyCardMeta} />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
